/**
 * AdminRecipes — BOM & 动态成本核算
 * 支持 BOM 配方管理、原料库维护、工艺路线配置、动态成本核算
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  FlaskConical, Plus, Trash2, RefreshCw, ChevronDown, ChevronUp,
  Loader2, AlertCircle, Edit3, Save, X, DollarSign, Package2, Download,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const ORANGE    = 'oklch(0.66 0.19 42)';
const CARD_BG   = '#FFFFFF';
const HEADER_BG = '#F0F2F5';

const IS: React.CSSProperties = {
  background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337',
  borderRadius: '4px', padding: '6px 10px', fontSize: '12px', outline: 'none', width: '100%',
};

// ── 类型定义 ──────────────────────────────────────────
interface RawMaterial {
  id: string; code: string; name: string; category: string;
  unit: string; unit_price: number; supplier: string | null;
  last_updated: string; notes: string | null;
}

interface ProcessRouting {
  id: string; code: string; name: string; step_order: number;
  duration_min: number | null; cost_per_kg: number; description: string | null;
}

interface ProductRecipe {
  id: string; product_code: string; product_name: string;
  material_type: string; spec_thickness: string | null; // 复用为密度规格
  spec_hardness: string | null; is_active: boolean; notes: string | null;
  // 技术预设（可选）
  spec_tolerance: string | null;
  spec_qa_req: string | null;
  spec_compliance: string | null;
  spec_acid_resistance: string | null;
  spec_ozone_resistance: string | null;
  spec_alkali_resistance: string | null;
  spec_compression_set: string | null;
  spec_oil_resistance: string | null;
  spec_temp_range: string | null;
  spec_seawater_resistance: string | null;
  spec_tensile_strength: string | null;
  spec_elongation: string | null; // 新增：断裂伸长率
}

interface RecipeMaterial {
  id: string; recipe_id: string; raw_material_id: string;
  ratio_phr: number; notes: string | null;
  raw_material?: RawMaterial;
}

interface RecipeRouting {
  id: string; recipe_id: string; routing_id: string;
  step_order: number; override_cost: number | null; notes: string | null;
  process_routing?: ProcessRouting;
}

const MATERIAL_TYPES = ['FKM', 'EPDM', 'NBR', 'NR', 'CR', 'ESD', '自定义'];
const CATEGORIES = ['生胶', '炭黑', '助剂', '硫化剂', '填充剂'];

// ── 小标签颜色配置（深色可读版）─────────────────────────
export const SPEC_TAG_CFG: Record<string, { color: string; bg: string; label: string }> = {
  spec_temp_range:          { color: '#B45309', bg: 'rgba(180,83,9,0.10)',    label: '温宽' },
  spec_acid_resistance:     { color: '#B91C1C', bg: 'rgba(185,28,28,0.09)',  label: '耐酸' },
  spec_alkali_resistance:   { color: '#6D28D9', bg: 'rgba(109,40,217,0.09)', label: '耐碱' },
  spec_oil_resistance:      { color: '#92400E', bg: 'rgba(146,64,14,0.09)',  label: '耐油' },
  spec_ozone_resistance:    { color: '#047857', bg: 'rgba(4,120,87,0.09)',   label: '耐臭氧' },
  spec_compression_set:     { color: '#1D4ED8', bg: 'rgba(29,78,216,0.09)', label: '压变' },
  spec_tensile_strength:    { color: '#BE185D', bg: 'rgba(190,24,93,0.09)', label: '拉强' },
  spec_seawater_resistance: { color: '#0E7490', bg: 'rgba(14,116,144,0.09)',label: '耐海水' },
  spec_compliance:          { color: '#15803D', bg: 'rgba(21,128,61,0.09)', label: '环保' },
  spec_qa_req:              { color: '#334155', bg: 'rgba(51,65,85,0.09)',   label: '质检' },
  spec_tolerance:           { color: '#7C3AED', bg: 'rgba(124,58,237,0.09)',label: '精度' },
  spec_elongation:          { color: '#0F766E', bg: 'rgba(15,118,110,0.09)',label: '伸长率' },
};

// ── 材质颜色映射（导出，供 AdminBOM 弹窗引用）──────────────
export const MATERIAL_TYPE_COLOR: Record<string, string> = {
  FKM: '#F97316', EPDM: '#3B82F6', NBR: '#8B5CF6',
  NR: '#10B981', CR: '#FBBF24', ESD: '#EC4899',
};

// ── 技术预设字段列表（新建/编辑共用）─────────────────────
const ALL_SPEC_KEYS: (keyof ProductRecipe)[] = [
  'spec_temp_range','spec_acid_resistance','spec_alkali_resistance',
  'spec_oil_resistance','spec_ozone_resistance','spec_compression_set',
  'spec_tensile_strength','spec_elongation','spec_seawater_resistance',
  'spec_compliance','spec_qa_req','spec_tolerance',
];

// ── 智能技术预设表单（新建+编辑统一使用）─────────────────
function TechSpecsSmartForm({
  values, onChange,
}: {
  values: Record<string, string>;
  onChange: (key: string, val: string) => void;
}) {
  const inp: React.CSSProperties = {
    background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337',
    borderRadius: '4px', padding: '5px 8px', fontSize: '12px', outline: 'none',
  };
  const label = (txt: string) => (
    <label className="text-[11px] mb-1 block font-medium" style={{ color: '#5A6780' }}>{txt}</label>
  );

  // ── 1. 温宽：双端数字输入 ────────────────────────────
  const tempVal = values['spec_temp_range'] ?? '';
  const [tempLo, setTempLo] = React.useState(() => tempVal.match(/(-?\d+)~/) ? tempVal.match(/(-?\d+)~/)![1] : '');
  const [tempHi, setTempHi] = React.useState(() => tempVal.match(/~\+?(-?\d+)/) ? tempVal.match(/~\+?(-?\d+)/)![1] : '');
  const commitTemp = (lo: string, hi: string) => {
    if (!lo && !hi) { onChange('spec_temp_range', ''); return; }
    onChange('spec_temp_range', `${lo || '?'}~${hi ? '+' + hi : '?'}°C`);
  };

  // ── 2. 耐酸/耐碱/耐油/耐海水：下拉（GB/T 1690-2010 测试等级）+自定义 ──────
  // 选项说明：依据 GB/T 1690-2010（等效 ISO 1817）浸泡试验后质量/体积变化率分级
  const RESIST_OPTIONS_MAP: Record<string, string[]> = {
    spec_acid_resistance: [
      'GB/T 1690 A级：质量变化≤2%，体积变化≤5%',
      'GB/T 1690 B级：质量变化≤5%，体积变化≤10%',
      'GB/T 1690 C级：质量变化≤10%，体积变化≤20%',
      'ISO 1817 Level 1：ΔV≤5%',
      'ISO 1817 Level 2：ΔV≤10%',
      'ASTM D471 Grade 1：ΔV≤10%',
      '不要求',
    ],
    spec_alkali_resistance: [
      'GB/T 1690 A级：质量变化≤2%，体积变化≤5%',
      'GB/T 1690 B级：质量变化≤5%，体积变化≤10%',
      'GB/T 1690 C级：质量变化≤10%，体积变化≤20%',
      'ISO 1817 Level 1：ΔV≤5%',
      'ISO 1817 Level 2：ΔV≤10%',
      'ASTM D471 Grade 1：ΔV≤10%',
      '不要求',
    ],
    spec_oil_resistance: [
      'GB/T 1690 A级：ΔV≤5%（矿物油）',
      'GB/T 1690 B级：ΔV≤10%（矿物油）',
      'GB/T 1690 C级：ΔV≤20%（矿物油）',
      'ISO 1817 油浸 Level 1：ΔV≤5%',
      'ISO 1817 油浸 Level 2：ΔV≤10%',
      'ASTM D471 IRM 903油：ΔV≤10%',
      'ASTM D471 IRM 902油：ΔV≤20%',
      '不要求',
    ],
    spec_seawater_resistance: [
      'GB/T 1690 海水浸泡 A级：ΔV≤5%',
      'GB/T 1690 海水浸泡 B级：ΔV≤10%',
      'GB/T 1690 海水浸泡 C级：ΔV≤20%',
      'ISO 1817 人工海水 Level 1：ΔV≤5%',
      'ISO 1817 人工海水 Level 2：ΔV≤10%',
      'ASTM D1141 海水模拟液：ΔV≤10%',
      '不要求',
    ],
  };
  const DEFAULT_RESIST_OPTIONS = [
    'GB/T 1690 A级：质量变化≤2%，体积变化≤5%',
    'GB/T 1690 B级：质量变化≤5%，体积变化≤10%',
    'GB/T 1690 C级：质量变化≤10%，体积变化≤20%',
    '不要求',
  ];
  const ResistSelect = ({ fieldKey, fieldLabel }: { fieldKey: string; fieldLabel: string }) => {
    const opts = RESIST_OPTIONS_MAP[fieldKey] ?? DEFAULT_RESIST_OPTIONS;
    const cur = values[fieldKey] ?? '';
    const isCustom = cur !== '' && !opts.includes(cur) && cur !== '不要求';
    const [custom, setCustom] = React.useState(isCustom ? cur : '');
    const [showCustom, setShowCustom] = React.useState(isCustom);
    return (
      <div>
        {label(fieldLabel)}
        <select style={{ ...inp, width: '100%', fontSize: 10 }}
          value={showCustom ? '自定义' : cur}
          onChange={e => {
            if (e.target.value === '自定义') { setShowCustom(true); onChange(fieldKey, custom); }
            else { setShowCustom(false); onChange(fieldKey, e.target.value); }
          }}>
          <option value="">— 未设置 —</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
          <option value="自定义">自定义…</option>
        </select>
        {showCustom && (
          <input style={{ ...inp, width: '100%', marginTop: 4 }}
            placeholder="自定义，如：10% H₂SO₄ 浸泡 70°C/24h ΔV≤8%"
            value={custom}
            onChange={e => { setCustom(e.target.value); onChange(fieldKey, e.target.value); }}
          />
        )}
      </div>
    );
  };

  // ── 3. 臭氧：快选按钮组 ────────────────────────────────
  const OZONE_OPTS = ['通过 100pphm/48h', '通过 50pphm/48h', '通过 25pphm/48h', '不要求'];
  const ozoneVal = values['spec_ozone_resistance'] ?? '';
  const isOzoneCustom = ozoneVal !== '' && !OZONE_OPTS.includes(ozoneVal);
  const [ozoneCustom, setOzoneCustom] = React.useState(isOzoneCustom ? ozoneVal : '');

  // ── 4. 压缩永久变形：滑块 + 数字显示 ──────────────────
  const compRaw = values['spec_compression_set'] ?? '';
  const compNum = parseInt(compRaw.replace(/[^0-9]/g, '')) || 25;

  // ── 5. 拉伸强度：数字 + 单位 ──────────────────────────
  const tensileRaw = values['spec_tensile_strength'] ?? '';
  const tensileNum = tensileRaw.replace(/[^0-9.]/g, '') || '';
  const tensileUnit = tensileRaw.includes('kN') ? 'kN/m' : 'MPa';

  // ── 6. 伸长率：数字 + % ────────────────────────────────
  const elongRaw = values['spec_elongation'] ?? '';
  const elongNum = elongRaw.replace(/[^0-9]/g, '') || '';

  // ── 7. 环保合规：多选 tag ──────────────────────────────
  const COMPLY_OPTS = ['RoHS', 'REACH', 'FDA', 'SVHC', 'California Prop 65'];
  const complyVal = values['spec_compliance'] ?? '';
  const complySelected = complyVal ? complyVal.split(' / ').filter(v => COMPLY_OPTS.includes(v)) : [];
  const complyCustomPart = complyVal ? complyVal.split(' / ').filter(v => !COMPLY_OPTS.includes(v)).join('') : '';
  const [complyCustom, setComplyCustom] = React.useState(complyCustomPart);
  const toggleComply = (opt: string) => {
    const next = complySelected.includes(opt) ? complySelected.filter(v => v !== opt) : [...complySelected, opt];
    const parts = [...next, complyCustom].filter(Boolean);
    onChange('spec_compliance', parts.join(' / '));
  };

  // ── 8. 质检标准：下拉 + 自定义 ────────────────────────
  const QA_OPTS = ['ISO 37', 'ISO 815', 'ASTM D412', 'ASTM D395', 'GB/T 528', '自定义'];
  const qaVal = values['spec_qa_req'] ?? '';
  const isQaCustom = qaVal !== '' && !QA_OPTS.slice(0,-1).includes(qaVal);
  const [qaCustom, setQaCustom] = React.useState(isQaCustom ? qaVal : '');
  const [qaShowCustom, setQaShowCustom] = React.useState(isQaCustom);

  // ── 9. 精度：下拉 + 自定义 ────────────────────────────
  const TOL_OPTS = ['±0.1mm', '±0.2mm', '±0.3mm', '±0.5mm', '±1.0mm', '自定义'];
  const tolVal = values['spec_tolerance'] ?? '';
  const isTolCustom = tolVal !== '' && !TOL_OPTS.slice(0,-1).includes(tolVal);
  const [tolCustom, setTolCustom] = React.useState(isTolCustom ? tolVal : '');
  const [tolShowCustom, setTolShowCustom] = React.useState(isTolCustom);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">

      {/* 1. 温宽 */}
      <div>
        {label('温度范围 Temperature Range')}
        <div className="flex items-center gap-1.5">
          <input type="number" min="-80" max="250" style={{ ...inp, width: '70px' }}
            placeholder="-20"
            value={tempLo}
            onChange={e => { setTempLo(e.target.value); commitTemp(e.target.value, tempHi); }}
          />
          <span style={{ color: '#5A6780', fontSize: 12 }}>~</span>
          <input type="number" min="-80" max="300" style={{ ...inp, width: '70px' }}
            placeholder="200"
            value={tempHi}
            onChange={e => { setTempHi(e.target.value); commitTemp(tempLo, e.target.value); }}
          />
          <span style={{ color: '#5A6780', fontSize: 11 }}>°C</span>
        </div>
        {tempVal && <div style={{ color: '#B45309', fontSize: 10, marginTop: 3 }}>{tempVal}</div>}
      </div>

      {/* 2. 耐酸 */}
      <ResistSelect fieldKey="spec_acid_resistance" fieldLabel="耐酸 Acid Resistance" />

      {/* 3. 耐碱 */}
      <ResistSelect fieldKey="spec_alkali_resistance" fieldLabel="耐碱 Alkali Resistance" />

      {/* 4. 耐油 */}
      <ResistSelect fieldKey="spec_oil_resistance" fieldLabel="耐油 Oil Resistance" />

      {/* 5. 耐海水 */}
      <ResistSelect fieldKey="spec_seawater_resistance" fieldLabel="耐海水 Seawater Resistance" />

      {/* 6. 臭氧 */}
      <div>
        {label('耐臭氧 Ozone Resistance')}
        <div className="flex flex-wrap gap-1 mb-1">
          {OZONE_OPTS.map(o => (
            <button key={o} type="button"
              onClick={() => onChange('spec_ozone_resistance', ozoneVal === o ? '' : o)}
              style={{
                fontSize: 10, padding: '3px 7px', borderRadius: 3, cursor: 'pointer',
                background: ozoneVal === o ? '#047857' : '#F0F2F5',
                color: ozoneVal === o ? '#fff' : '#5A6780',
                border: `1px solid ${ozoneVal === o ? '#047857' : '#D0D5E2'}`,
              }}>{o}</button>
          ))}
        </div>
        {isOzoneCustom && (
          <input style={{ ...inp, width: '100%' }} placeholder="自定义臭氧条件"
            value={ozoneCustom}
            onChange={e => { setOzoneCustom(e.target.value); onChange('spec_ozone_resistance', e.target.value); }}
          />
        )}
        {!isOzoneCustom && (
          <button type="button" style={{ fontSize: 10, color: '#5A6780', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            onClick={() => { onChange('spec_ozone_resistance', ''); setOzoneCustom(''); }}>自定义...</button>
        )}
      </div>

      {/* 7. 压缩永久变形：滑块 */}
      <div>
        {label('压缩永久变形 Comp. Set')}
        <div className="flex items-center gap-2">
          <input type="range" min="5" max="60" step="5"
            value={compNum}
            onChange={e => onChange('spec_compression_set', `≤${e.target.value}% @70°C`)}
            style={{ flex: 1, accentColor: '#1D4ED8' }}
          />
          <span style={{ color: '#1D4ED8', fontSize: 12, fontWeight: 700, minWidth: 50 }}>≤{compNum}%</span>
        </div>
        <div style={{ color: '#5A6780', fontSize: 10, marginTop: 2 }}>当前值：{values['spec_compression_set'] || '未设置'}</div>
      </div>

      {/* 8. 拉伸强度：滑块 (1–30 MPa，含单位切换) */}
      <div>
        {label('拉伸强度 Tensile Strength')}
        <div className="flex items-center gap-2">
          <input type="range" min="1" max="30" step="0.5"
            value={tensileNum || '10'}
            onChange={e => onChange('spec_tensile_strength', `≥${e.target.value} ${tensileUnit}`)}
            style={{ flex: 1, accentColor: '#BE185D' }}
          />
          <span style={{ color: '#BE185D', fontSize: 12, fontWeight: 700, minWidth: 52 }}>≥{tensileNum || '10'} {tensileUnit}</span>
        </div>
        <div className="flex items-center gap-1 mt-1">
          <span style={{ color: '#5A6780', fontSize: 10 }}>单位：</span>
          {['MPa', 'kN/m'].map(u => (
            <button key={u} type="button"
              onClick={() => onChange('spec_tensile_strength', tensileNum ? `≥${tensileNum} ${u}` : '')}
              style={{
                fontSize: 10, padding: '2px 6px', borderRadius: 3, cursor: 'pointer',
                background: tensileUnit === u ? '#BE185D' : '#F0F2F5',
                color: tensileUnit === u ? '#fff' : '#5A6780',
                border: `1px solid ${tensileUnit === u ? '#BE185D' : '#D0D5E2'}`,
              }}>{u}</button>
          ))}
          <span style={{ color: '#5A6780', fontSize: 10, marginLeft: 'auto' }}>当前：{values['spec_tensile_strength'] || '未设置'}</span>
        </div>
      </div>

      {/* 9. 断裂伸长率：滑块 (50–800 %) */}
      <div>
        {label('断裂伸长率 Elongation at Break')}
        <div className="flex items-center gap-2">
          <input type="range" min="50" max="800" step="10"
            value={elongNum || '200'}
            onChange={e => onChange('spec_elongation', `≥${e.target.value}%`)}
            style={{ flex: 1, accentColor: '#0F766E' }}
          />
          <span style={{ color: '#0F766E', fontSize: 12, fontWeight: 700, minWidth: 48 }}>≥{elongNum || '200'}%</span>
        </div>
        <div style={{ color: '#5A6780', fontSize: 10, marginTop: 2 }}>当前：{values['spec_elongation'] || '未设置'}</div>
      </div>

      {/* 10. 环保合规：多选 tag */}
      <div>
        {label('环保合规 Compliance')}
        <div className="flex flex-wrap gap-1 mb-1">
          {COMPLY_OPTS.map(o => (
            <button key={o} type="button"
              onClick={() => toggleComply(o)}
              style={{
                fontSize: 10, padding: '3px 7px', borderRadius: 3, cursor: 'pointer',
                background: complySelected.includes(o) ? '#15803D' : '#F0F2F5',
                color: complySelected.includes(o) ? '#fff' : '#5A6780',
                border: `1px solid ${complySelected.includes(o) ? '#15803D' : '#D0D5E2'}`,
              }}>{o}</button>
          ))}
        </div>
        <input style={{ ...inp, width: '100%' }} placeholder="其他合规要求（选填）"
          value={complyCustom}
          onChange={e => {
            setComplyCustom(e.target.value);
            const parts = [...complySelected, e.target.value].filter(Boolean);
            onChange('spec_compliance', parts.join(' / '));
          }}
        />
      </div>

      {/* 11. 质检标准 */}
      <div>
        {label('质检标准 QA Requirement')}
        <select style={{ ...inp, width: '100%' }}
          value={qaShowCustom ? '自定义' : qaVal}
          onChange={e => {
            if (e.target.value === '自定义') { setQaShowCustom(true); onChange('spec_qa_req', qaCustom); }
            else { setQaShowCustom(false); onChange('spec_qa_req', e.target.value); }
          }}>
          <option value="">— 未设置 —</option>
          {QA_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {qaShowCustom && (
          <input style={{ ...inp, width: '100%', marginTop: 4 }} placeholder="自定义质检标准"
            value={qaCustom}
            onChange={e => { setQaCustom(e.target.value); onChange('spec_qa_req', e.target.value); }}
          />
        )}
      </div>

      {/* 12. 精度 */}
      <div>
        {label('尺寸精度 Tolerance')}
        <select style={{ ...inp, width: '100%' }}
          value={tolShowCustom ? '自定义' : tolVal}
          onChange={e => {
            if (e.target.value === '自定义') { setTolShowCustom(true); onChange('spec_tolerance', tolCustom); }
            else { setTolShowCustom(false); onChange('spec_tolerance', e.target.value); }
          }}>
          <option value="">— 未设置 —</option>
          {TOL_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {tolShowCustom && (
          <input style={{ ...inp, width: '100%', marginTop: 4 }} placeholder="自定义精度，如 ±0.15mm"
            value={tolCustom}
            onChange={e => { setTolCustom(e.target.value); onChange('spec_tolerance', e.target.value); }}
          />
        )}
      </div>

    </div>
  );
}

// ── Tab 枚举 ──────────────────────────────────────────
type Tab = 'recipes' | 'materials' | 'routings';

// ── 原料库管理 ────────────────────────────────────────
function MaterialsTab({ materials, onRefresh }: { materials: RawMaterial[]; onRefresh: () => void }) {
  const [editing, setEditing] = useState<RawMaterial | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<RawMaterial>>({});

  const openAdd = () => { setForm({ unit: 'kg', unit_price: 0 }); setAdding(true); setEditing(null); };
  const openEdit = (m: RawMaterial) => { setForm(m); setEditing(m); setAdding(false); };
  const closeForm = () => { setAdding(false); setEditing(null); setForm({}); };

  const save = async () => {
    if (!form.code || !form.name || !form.category) return;
    setSaving(true);
    if (editing) {
      await supabase.from('raw_materials').update({
        code: form.code, name: form.name, category: form.category,
        unit: form.unit ?? 'kg', unit_price: form.unit_price ?? 0,
        supplier: form.supplier ?? null, notes: form.notes ?? null,
        last_updated: new Date().toISOString(),
      }).eq('id', editing.id);
    } else {
      await supabase.from('raw_materials').insert({
        code: form.code, name: form.name, category: form.category,
        unit: form.unit ?? 'kg', unit_price: form.unit_price ?? 0,
        supplier: form.supplier ?? null, notes: form.notes ?? null,
      });
    }
    setSaving(false); closeForm(); onRefresh();
  };

  const del = async (id: string) => {
    if (!confirm('确认删除此原料？如已被配方引用则无法删除。')) return;
    const { error } = await supabase.from('raw_materials').delete().eq('id', id);
    if (error) alert('删除失败：' + error.message);
    else onRefresh();
  };

  const catColor: Record<string, string> = {
    '生胶': ORANGE, '炭黑': '#8A9BB5', '助剂': '#60A5FA',
    '硫化剂': '#C084FC', '填充剂': '#34D399',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>
          基础原料库 ({materials.length} 条)
        </p>
        <button onClick={openAdd}
          className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm"
          style={{ background: ORANGE, color: '#fff' }}>
          <Plus className="w-3.5 h-3.5" /> 新增原料
        </button>
      </div>

      {/* 表单 */}
      {(adding || editing) && (
        <div className="rounded-sm border p-4 space-y-3" style={{ background: '#F7F8FA', borderColor: `${ORANGE}50` }}>
          <p className="text-xs font-bold" style={{ color: ORANGE }}>{adding ? '新增原料' : '编辑原料'}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>编号 *</label>
              <input value={form.code ?? ''} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} style={IS} placeholder="RM-XXX-01" /></div>
            <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>名称 *</label>
              <input value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={IS} placeholder="原料中文名" /></div>
            <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>类别 *</label>
              <select value={form.category ?? ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={IS}>
                <option value="">选择类别</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>单位</label>
              <input value={form.unit ?? 'kg'} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} style={IS} /></div>
            <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>当前单价（元）</label>
              <input type="number" value={form.unit_price ?? ''} onChange={e => setForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))} style={IS} /></div>
            <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>供应商</label>
              <input value={form.supplier ?? ''} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} style={IS} /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving}
              className="flex items-center gap-1.5 text-xs font-bold px-4 py-1.5 rounded-sm disabled:opacity-60"
              style={{ background: ORANGE, color: '#fff' }}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} 保存
            </button>
            <button onClick={closeForm} className="flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-sm"
              style={{ background: '#F7F8FA', color: '#5A6780', border: '1px solid #D0D5E2' }}>
              <X className="w-3.5 h-3.5" /> 取消
            </button>
          </div>
        </div>
      )}

      {/* 列表 */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
              {['编号', '名称', '类别', '单价', '供应商', '更新时间', '操作'].map(h => (
                <th key={h} className="text-left py-2 pr-4 font-mono" style={{ color: '#5A6780' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {materials.map(m => (
              <tr key={m.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td className="py-2 pr-4 font-mono" style={{ color: '#5A6780' }}>{m.code}</td>
                <td className="py-2 pr-4 font-medium" style={{ color: '#1A2337' }}>{m.name}</td>
                <td className="py-2 pr-4">
                  <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold"
                    style={{ background: `${catColor[m.category] ?? '#8A9BB5'}18`, color: catColor[m.category] ?? '#8A9BB5' }}>
                    {m.category}
                  </span>
                </td>
                <td className="py-2 pr-4 tabular-nums font-bold" style={{ color: ORANGE }}>¥{Number(m.unit_price).toFixed(2)}/{m.unit}</td>
                <td className="py-2 pr-4" style={{ color: '#C5D0E0' }}>{m.supplier ?? '—'}</td>
                <td className="py-2 pr-4 tabular-nums" style={{ color: '#5A6780' }}>{new Date(m.last_updated).toLocaleDateString('zh-CN')}</td>
                <td className="py-2">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(m)} className="p-1 rounded" style={{ color: '#60A5FA' }}><Edit3 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(m.id)} className="p-1 rounded" style={{ color: '#F87171' }}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 配方详情（成本核算面板）────────────────────────────
function RecipeDetail({
  recipe, materials, routings,
}: {
  recipe: ProductRecipe; materials: RawMaterial[]; routings: ProcessRouting[];
}) {
  const [recipeMaterials, setRecipeMaterials] = useState<RecipeMaterial[]>([]);
  const [recipeRoutings, setRecipeRoutings] = useState<RecipeRouting[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingMat, setAddingMat] = useState(false);
  const [addingRt, setAddingRt] = useState(false);
  const [matForm, setMatForm] = useState({ raw_material_id: '', ratio_phr: '' });
  const [rtForm, setRtForm] = useState({ routing_id: '', step_order: '1', override_cost: '' });
  // 技术预设编辑状态
  const [editingSpecs, setEditingSpecs] = useState(false);
  const [specsForm, setSpecsForm] = useState<Record<string, string>>(() =>
    Object.fromEntries(ALL_SPEC_KEYS.map(k => [k, (recipe[k] as string | null) ?? '']))
  );
  const [savingSpecs, setSavingSpecs] = useState(false);

  // TECH_SPEC_FIELDS 仅用于显示区遍历，编辑区改用 TechSpecsSmartForm
  const TECH_SPEC_DISPLAY_FIELDS = ALL_SPEC_KEYS.map(k => ({
    key: k,
    label: SPEC_TAG_CFG[k]?.label ?? k,
  }));

  const saveSpecs = async () => {
    setSavingSpecs(true);
    const patch: Record<string, string | null> = {};
    ALL_SPEC_KEYS.forEach(k => {
      patch[k] = (specsForm[k] ?? '').trim() || null;
    });
    await supabase.from('product_recipes').update(patch).eq('id', recipe.id);
    Object.assign(recipe, patch);
    setSavingSpecs(false);
    setEditingSpecs(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: rms }, { data: rrs }] = await Promise.all([
      supabase.from('recipe_materials').select('*, raw_materials(*)').eq('recipe_id', recipe.id),
      supabase.from('recipe_routings').select('*, process_routings(*)').eq('recipe_id', recipe.id).order('step_order'),
    ]);
    setRecipeMaterials((rms ?? []).map((r: Record<string, unknown>) => ({
      ...(r as object), raw_material: r.raw_materials as RawMaterial,
    })) as RecipeMaterial[]);
    setRecipeRoutings((rrs ?? []).map((r: Record<string, unknown>) => ({
      ...(r as object), process_routing: r.process_routings as ProcessRouting,
    })) as RecipeRouting[]);
    setLoading(false);
  }, [recipe.id]);

  useEffect(() => { load(); }, [load]);

  // 计算动态成本
  const totalPhr = recipeMaterials.reduce((s, r) => s + Number(r.ratio_phr), 0);
  const matCostPerKg = totalPhr > 0
    ? recipeMaterials.reduce((s, r) => {
        const price = r.raw_material?.unit_price ?? 0;
        return s + (Number(r.ratio_phr) / totalPhr) * Number(price);
      }, 0)
    : 0;
  const processCostPerKg = recipeRoutings.reduce((s, r) => {
    const cost = r.override_cost != null ? Number(r.override_cost) : (r.process_routing?.cost_per_kg ?? 0);
    return s + Number(cost);
  }, 0);
  const totalCostPerKg = matCostPerKg + processCostPerKg;
  const suggestedPrice = totalCostPerKg * 1.35; // 建议35%毛利

  const addMat = async () => {
    if (!matForm.raw_material_id || !matForm.ratio_phr) return;
    await supabase.from('recipe_materials').insert({
      recipe_id: recipe.id, raw_material_id: matForm.raw_material_id,
      ratio_phr: parseFloat(matForm.ratio_phr),
    });
    setMatForm({ raw_material_id: '', ratio_phr: '' }); setAddingMat(false); load();
  };

  const addRt = async () => {
    if (!rtForm.routing_id) return;
    await supabase.from('recipe_routings').insert({
      recipe_id: recipe.id, routing_id: rtForm.routing_id,
      step_order: parseInt(rtForm.step_order) || 1,
      override_cost: rtForm.override_cost ? parseFloat(rtForm.override_cost) : null,
    });
    setRtForm({ routing_id: '', step_order: '1', override_cost: '' }); setAddingRt(false); load();
  };

  const delMat = async (id: string) => { await supabase.from('recipe_materials').delete().eq('id', id); load(); };
  const delRt = async (id: string) => { await supabase.from('recipe_routings').delete().eq('id', id); load(); };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: ORANGE }} /></div>;

  // ── 打印函数：新窗口 + window.print()，用户可另存为PDF ──────────
  const printRecipe = () => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const docId = `DOC-PP-${recipe.product_code}-${now.getFullYear()}`;

    // ── 原料行 HTML（严格参考图表格样式）──
    const matRows = recipeMaterials.length === 0
      ? `<tr><td colspan="6" style="padding:14px 12px;text-align:center;color:#334155;font-style:italic;">暂无原料配比数据</td></tr>`
      : recipeMaterials.map((rm, i) => {
          const pct = totalPhr > 0 ? (Number(rm.ratio_phr) / totalPhr * 100).toFixed(1) : '0.0';
          const contribution = totalPhr > 0
            ? ((Number(rm.ratio_phr) / totalPhr) * Number(rm.raw_material?.unit_price ?? 0)).toFixed(4)
            : '0.0000';
          const rowBg = i % 2 === 0 ? '#0D1B2A' : '#091524';
          return `<tr style="background:${rowBg};">
            <td style="padding:9px 12px;color:#CBD5E1;font-weight:600;border-bottom:1px solid #1E3050;">${rm.raw_material?.name ?? '—'}</td>
            <td style="padding:9px 12px;border-bottom:1px solid #1E3050;">
              <span style="background:rgba(96,165,250,0.12);color:#60A5FA;padding:2px 7px;border-radius:2px;font-size:9.5px;font-weight:700;">${rm.raw_material?.category ?? '—'}</span>
            </td>
            <td style="padding:9px 12px;color:#F97316;font-weight:800;text-align:right;font-size:13px;border-bottom:1px solid #1E3050;">${Number(rm.ratio_phr).toFixed(2)}</td>
            <td style="padding:9px 12px;color:#94A3B8;text-align:right;border-bottom:1px solid #1E3050;">${pct}%</td>
            <td style="padding:9px 12px;color:#94A3B8;text-align:right;border-bottom:1px solid #1E3050;">¥ ${Number(rm.raw_material?.unit_price ?? 0).toFixed(2)}</td>
            <td style="padding:9px 12px;color:#60A5FA;font-weight:700;text-align:right;border-bottom:1px solid #1E3050;">¥ ${contribution}</td>
          </tr>`;
        }).join('');

    // ── 工艺行 HTML（橙色圆形序号）──
    const rtRows = recipeRoutings.length === 0
      ? `<tr><td colspan="4" style="padding:14px 12px;text-align:center;color:#334155;font-style:italic;">暂无工艺路线数据</td></tr>`
      : recipeRoutings.map((rr, i) => {
          const cost = rr.override_cost != null ? Number(rr.override_cost) : (rr.process_routing?.cost_per_kg ?? 0);
          const isOverride = rr.override_cost != null;
          const rowBg = i % 2 === 0 ? '#0D1B2A' : '#091524';
          return `<tr style="background:${rowBg};">
            <td style="padding:9px 12px;text-align:center;border-bottom:1px solid #1E3050;">
              <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:rgba(249,115,22,0.15);color:#F97316;font-size:11px;font-weight:800;">${i + 1}</span>
            </td>
            <td style="padding:9px 12px;color:#CBD5E1;font-weight:600;border-bottom:1px solid #1E3050;">${rr.process_routing?.name ?? '—'}</td>
            <td style="padding:9px 12px;color:#94A3B8;text-align:right;border-bottom:1px solid #1E3050;">${rr.process_routing?.duration_min != null ? rr.process_routing.duration_min + ' min' : '—'}</td>
            <td style="padding:9px 12px;font-weight:700;text-align:right;border-bottom:1px solid #1E3050;color:${isOverride ? '#FBBF24' : '#94A3B8'};">¥ ${Number(cost).toFixed(2)}/kg${isOverride ? ' ★' : ''}</td>
          </tr>`;
        }).join('');

    // ── 修订历史固定行 ──
    const revRows = `<tr style="background:#091524;">
      <td style="padding:7px 12px;color:#475569;border-bottom:1px solid #1E3050;">A</td>
      <td style="padding:7px 12px;color:#475569;border-bottom:1px solid #1E3050;">${dateStr}</td>
      <td style="padding:7px 12px;color:#94A3B8;border-bottom:1px solid #1E3050;">初始发布 / Initial Release</td>
      <td style="padding:7px 12px;color:#475569;border-bottom:1px solid #1E3050;">系统自动生成</td>
    </tr>`;

    const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8"/>
<title>配方明细 - ${recipe.product_code}</title>
<style>
* { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
body { font-family:'Segoe UI','Microsoft YaHei',Arial,sans-serif; background:#071120; color:#CBD5E1; font-size:12px; }
.page { width:210mm; min-height:297mm; margin:0 auto; padding:14mm 15mm 12mm 15mm; background:#071120; }
.header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:12px; border-bottom:2px solid #F97316; margin-bottom:16px; }
.logo { font-size:22px; font-weight:900; color:#F97316; letter-spacing:0.08em; }
.logo-sub { font-size:9.5px; color:#64748B; margin-top:3px; line-height:1.6; }
.header-right { text-align:right; }
.doc-title { font-size:15px; font-weight:800; color:#E2E8F0; }
.doc-subtitle { font-size:9px; color:#64748B; margin-top:2px; text-transform:uppercase; letter-spacing:0.06em; }
.doc-meta { margin-top:8px; display:flex; gap:12px; justify-content:flex-end; align-items:center; flex-wrap:wrap; }
.meta-item { font-size:9px; color:#64748B; }
.meta-item b { color:#94A3B8; }
.badge-int { background:rgba(249,115,22,0.15); color:#F97316; border:1px solid rgba(249,115,22,0.35); padding:2px 9px; border-radius:2px; font-size:9px; font-weight:800; letter-spacing:0.08em; }
.info-grid { display:grid; grid-template-columns:1fr 1fr 1fr; border:1px solid #1E3050; border-radius:3px; margin-bottom:16px; overflow:hidden; }
.info-cell { padding:11px 14px; background:#0D1B2A; }
.info-cell:not(:last-child) { border-right:1px solid #1E3050; }
.info-label { font-size:8.5px; color:#475569; font-weight:700; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:5px; }
.info-value { font-size:14px; font-weight:800; color:#E2E8F0; }
.info-value.ac { color:#F97316; }
.info-value.bl { color:#60A5FA; }
.info-sub { font-size:10px; color:#64748B; margin-top:3px; }
.sec { display:flex; align-items:center; gap:8px; margin:14px 0 10px; }
.sec-bar { width:3px; height:14px; background:#F97316; border-radius:1px; flex-shrink:0; }
.sec-bar.dim { background:#334155; }
.sec-title { font-size:9px; font-weight:800; text-transform:uppercase; letter-spacing:0.12em; color:#64748B; }
.sec-title span { color:#94A3B8; }
.cost-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:14px; }
.cost-card { background:#0D1B2A; border:1px solid #1E3050; border-radius:3px; padding:11px 13px; }
.cost-card.hi { border-color:rgba(249,115,22,0.4); }
.c-label { font-size:8.5px; color:#475569; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:6px; }
.c-value { font-size:18px; font-weight:900; line-height:1; }
.c-unit { font-size:9px; font-weight:400; color:#475569; margin-left:1px; }
table { width:100%; border-collapse:collapse; }
th { background:#0D1B2A; color:#64748B; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.07em; padding:8px 12px; text-align:left; border-bottom:2px solid #1E3050; }
th.r { text-align:right; }
.cat-b { background:rgba(96,165,250,0.12); color:#60A5FA; padding:2px 7px; border-radius:2px; font-size:9.5px; font-weight:700; }
.rev-th { background:#091524; }
.divider { border:none; border-top:1px solid #1E3050; margin:14px 0; }
.footer { margin-top:18px; border-top:1px solid #1E3050; padding-top:10px; display:flex; justify-content:space-between; }
.foot-l { font-size:9px; color:#334155; line-height:1.7; max-width:65%; }
.foot-r { text-align:right; font-size:9px; color:#334155; line-height:1.7; }
.foot-brand { color:#475569; font-weight:700; }
@media print { body{-webkit-print-color-adjust:exact;print-color-adjust:exact;} .page{margin:0;padding:10mm 13mm;} }
</style></head><body>
<div class="page">

<!-- ① HEADER -->
<div class="header">
  <div>
    <div class="logo">POLY-PRIME™</div>
    <div class="logo-sub">BEIJING POLY-PRIME INDUSTRIAL TECHNOLOGY CO., LTD.<br/>博元合成（北京）工业科技有限公司</div>
  </div>
  <div class="header-right">
    <div class="doc-title">产品配方明细表</div>
    <div class="doc-subtitle">Compound Recipe Data Sheet</div>
    <div class="doc-meta">
      <div class="meta-item">文档编号 <b>${docId}</b></div>
      <div class="meta-item">版本 <b>Rev A</b></div>
      <div class="meta-item">日期 <b>${dateStr}</b></div>
      <div class="badge-int">INTERNAL</div>
    </div>
  </div>
</div>

<!-- ② 核心产品信息 3栏 -->
<div class="info-grid">
  <div class="info-cell">
    <div class="info-label">Product Code · 产品编号</div>
    <div class="info-value ac">${recipe.product_code}</div>
  </div>
  <div class="info-cell">
    <div class="info-label">Product Name · 产品名称</div>
    <div class="info-value">${recipe.product_name}</div>
    ${recipe.notes ? `<div class="info-sub">${recipe.notes}</div>` : ''}
  </div>
  <div class="info-cell">
    <div class="info-label">Material Type · 材料体系</div>
    <div class="info-value bl">${recipe.material_type}</div>
    <div class="info-sub">${[recipe.spec_thickness && ('密度: ' + recipe.spec_thickness), recipe.spec_hardness && ('硬度: ' + recipe.spec_hardness)].filter(Boolean).join(' · ') || '规格待定'}</div>
  </div>
</div>

<!-- ③ 成本核算 4卡片 -->
<div class="sec"><div class="sec-bar"></div><div class="sec-title">动态成本核算 · <span>Dynamic Cost Analysis</span></div></div>
<div class="cost-grid">
  <div class="cost-card"><div class="c-label">原料成本</div><div class="c-value" style="color:#60A5FA;">¥ ${matCostPerKg.toFixed(2)}<span class="c-unit">/kg</span></div></div>
  <div class="cost-card"><div class="c-label">工艺加工费</div><div class="c-value" style="color:#C084FC;">¥ ${processCostPerKg.toFixed(2)}<span class="c-unit">/kg</span></div></div>
  <div class="cost-card hi"><div class="c-label">综合成本（含工艺）</div><div class="c-value" style="color:#F97316;">¥ ${totalCostPerKg.toFixed(2)}<span class="c-unit">/kg</span></div></div>
  <div class="cost-card"><div class="c-label">建议报价（毛利 35%）</div><div class="c-value" style="color:#34D399;">¥ ${suggestedPrice.toFixed(2)}<span class="c-unit">/kg</span></div></div>
</div>

<!-- ④ 原料配比表格 -->
<div class="sec"><div class="sec-bar"></div><div class="sec-title">原料配比明细 · <span>Ingredients Table (PHR)</span></div></div>
<table style="margin-bottom:14px;">
  <thead><tr>
    <th>原料名称 · Ingredient</th><th>类别</th>
    <th class="r">用量 PHR</th><th class="r">配比占比</th>
    <th class="r">单价 (¥/kg)</th><th class="r">成本贡献 (¥/kg)</th>
  </tr></thead>
  <tbody>${matRows}</tbody>
</table>

<!-- ⑤ 工艺路线表格 -->
<div class="sec"><div class="sec-bar"></div><div class="sec-title">工艺路线 · <span>Process Routing</span></div></div>
<table style="margin-bottom:14px;">
  <thead><tr>
    <th style="width:44px;text-align:center;">步骤</th>
    <th>工艺名称 · Process Step</th>
    <th class="r">加工时长</th><th class="r">加工成本</th>
  </tr></thead>
  <tbody>${rtRows}</tbody>
</table>

<div class="divider"></div>

<!-- ⑥ 修订历史 -->
<div class="sec"><div class="sec-bar dim"></div><div class="sec-title" style="color:#334155;">修订历史 · <span style="color:#1E3050;">Revision History</span></div></div>
<table style="margin-bottom:0;">
  <thead><tr>
    <th class="rev-th" style="width:60px;">版本 Rev</th>
    <th class="rev-th" style="width:100px;">日期 Date</th>
    <th class="rev-th">变更说明</th>
    <th class="rev-th" style="width:120px;">修订人</th>
  </tr></thead>
  <tbody>${revRows}</tbody>
</table>

<!-- ⑦ 页脚 -->
<div class="footer">
  <div class="foot-l">本文件含 POLY-PRIME™ 配方与成本专有信息，仅限内部授权人员查阅。未经许可，严禁向第三方披露。成本数据基于当前市场原料单价动态计算，实际值因市场波动而有所差异。★ 标注工艺使用自定义覆盖成本。</div>
  <div class="foot-r"><div class="foot-brand">POLY-PRIME™</div>博元合成（北京）工业科技有限公司<br/>info@poly-prime.com<br/><span style="color:#1E3050;">Generated: ${now.toLocaleString('zh-CN')}</span></div>
</div>

</div></body></html>`;

    // ── 新窗口写入 HTML，触发浏览器打印（用户可另存为PDF）──
    const printWin = window.open('', '_blank', 'width=900,height=750');
    if (!printWin) { alert('弹窗被阻止，请允许弹出窗口后重试'); return; }
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => { printWin.print(); }, 800);
  };

  return (
    <div className="space-y-5 mt-4">
      {/* 成本核算卡 + 导出按钮 */}
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>动态成本核算</p>
        <button
          onClick={printRecipe}
          className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm"
          style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
          title="在新窗口打印配方明细，打印对话框中选择'另存为PDF'可保存"
        >
          <Download className="w-3.5 h-3.5" style={{ color: ORANGE }} /> 打印配方
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: '原料成本', value: `¥${matCostPerKg.toFixed(2)}/kg`, color: '#60A5FA' },
          { label: '工艺加工费', value: `¥${processCostPerKg.toFixed(2)}/kg`, color: '#C084FC' },
          { label: '综合成本', value: `¥${totalCostPerKg.toFixed(2)}/kg`, color: ORANGE },
          { label: '建议报价 (毛利35%)', value: `¥${suggestedPrice.toFixed(2)}/kg`, color: '#34D399' },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-sm border border-border p-3" style={{ background: '#F7F8FA' }}>
            <p className="text-[10px] font-mono" style={{ color: '#5A6780' }}>{label}</p>
            <p className="text-lg font-black mt-1 tabular-nums" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* ── 技术预设面板 ── */}
      <div className="rounded-sm border border-border p-4" style={{ background: '#F7F8FA' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div style={{ width: 3, height: 12, background: ORANGE, borderRadius: 1 }} />
            <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>技术预设 · Technical Specs</p>
          </div>
          <button
            onClick={() => setEditingSpecs(e => !e)}
            className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-sm"
            style={{ background: editingSpecs ? `${ORANGE}20` : '#F7F8FA', color: editingSpecs ? ORANGE : '#5A6780', border: `1px solid ${editingSpecs ? ORANGE + '50' : '#D0D5E2'}` }}
          >
            <Edit3 className="w-3 h-3" /> {editingSpecs ? '取消编辑' : '编辑'}
          </button>
        </div>

        {editingSpecs ? (
          <div className="space-y-3">
            <TechSpecsSmartForm
              values={specsForm}
              onChange={(k, v) => setSpecsForm(f => ({ ...f, [k]: v }))}
            />
            <div className="flex gap-2 pt-1">
              <button onClick={saveSpecs} disabled={savingSpecs}
                className="flex items-center gap-1.5 text-xs font-bold px-4 py-1.5 rounded-sm disabled:opacity-60"
                style={{ background: ORANGE, color: '#fff' }}>
                {savingSpecs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} 保存技术预设
              </button>
              <button onClick={() => setEditingSpecs(false)} className="text-xs px-4 py-1.5 rounded-sm"
                style={{ background: '#FFFFFF', color: '#5A6780', border: '1px solid #D0D5E2' }}>取消</button>
            </div>
          </div>
        ) : (
          (() => {
            const filled = TECH_SPEC_DISPLAY_FIELDS.filter(({ key }) => !!(recipe as unknown as Record<string, unknown>)[key]);
            return filled.length === 0 ? (
              <p className="text-xs" style={{ color: '#3A4A5C' }}>暂未填写技术预设，点击「编辑」添加</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {filled.map(({ key }) => {
                  const cfg = SPEC_TAG_CFG[key];
                  const val = (recipe as unknown as Record<string, unknown>)[key] as string;
                  return (
                    <span key={key} style={{
                      background: cfg?.bg ?? 'rgba(90,103,128,0.1)',
                      color: cfg?.color ?? '#5A6780',
                      border: `1px solid ${cfg?.color ?? '#5A6780'}40`,
                      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                    }}>
                      {cfg?.label ?? key}: {val}
                    </span>
                  );
                })}
              </div>
            );
          })()
        )}
      </div>

      {/* 配方原料明细 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>原料配比（PHR）</p>
          <button onClick={() => setAddingMat(a => !a)} className="flex items-center gap-1 text-xs px-2 py-1 rounded-sm"
            style={{ background: `${ORANGE}20`, color: ORANGE }}>
            <Plus className="w-3 h-3" /> 添加原料
          </button>
        </div>
        {addingMat && (
          <div className="flex gap-2 mb-2 flex-wrap">
            <select value={matForm.raw_material_id} onChange={e => setMatForm(f => ({ ...f, raw_material_id: e.target.value }))}
              style={{ ...IS, width: '200px' }}>
              <option value="">选择原料</option>
              {materials.map(m => <option key={m.id} value={m.id}>{m.name} (¥{m.unit_price}/{m.unit})</option>)}
            </select>
            <input type="number" value={matForm.ratio_phr} onChange={e => setMatForm(f => ({ ...f, ratio_phr: e.target.value }))}
              placeholder="用量 PHR" style={{ ...IS, width: '100px' }} />
            <button onClick={addMat} className="px-3 py-1 text-xs font-bold rounded-sm" style={{ background: ORANGE, color: '#fff' }}>添加</button>
            <button onClick={() => setAddingMat(false)} className="px-3 py-1 text-xs rounded-sm" style={{ background: '#FFFFFF', color: '#5A6780', border: '1px solid #D0D5E2' }}>取消</button>
          </div>
        )}
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
              {['原料名称', '类别', '用量(PHR)', '当前单价', '成本占比', ''].map(h => (
                <th key={h} className="text-left py-1.5 pr-3 font-mono" style={{ color: '#5A6780' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recipeMaterials.map(rm => {
              const pct = totalPhr > 0 ? (Number(rm.ratio_phr) / totalPhr * 100).toFixed(1) : '0';
              return (
                <tr key={rm.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td className="py-1.5 pr-3 font-medium" style={{ color: '#1A2337' }}>{rm.raw_material?.name ?? '—'}</td>
                  <td className="py-1.5 pr-3" style={{ color: '#5A6780' }}>{rm.raw_material?.category ?? '—'}</td>
                  <td className="py-1.5 pr-3 tabular-nums font-bold" style={{ color: ORANGE }}>{rm.ratio_phr}</td>
                  <td className="py-1.5 pr-3 tabular-nums" style={{ color: '#C5D0E0' }}>¥{Number(rm.raw_material?.unit_price ?? 0).toFixed(2)}</td>
                  <td className="py-1.5 pr-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: '#FFFFFF' }}>
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ORANGE }} />
                      </div>
                      <span style={{ color: '#5A6780' }}>{pct}%</span>
                    </div>
                  </td>
                  <td className="py-1.5">
                    <button onClick={() => delMat(rm.id)} className="p-1" style={{ color: '#F87171' }}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 工艺路线明细 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>工艺路线</p>
          <button onClick={() => setAddingRt(a => !a)} className="flex items-center gap-1 text-xs px-2 py-1 rounded-sm"
            style={{ background: 'rgba(96,165,250,0.15)', color: '#60A5FA' }}>
            <Plus className="w-3 h-3" /> 添加工艺
          </button>
        </div>
        {addingRt && (
          <div className="flex gap-2 mb-2 flex-wrap">
            <select value={rtForm.routing_id} onChange={e => setRtForm(f => ({ ...f, routing_id: e.target.value }))}
              style={{ ...IS, width: '180px' }}>
              <option value="">选择工艺</option>
              {routings.map(r => <option key={r.id} value={r.id}>{r.name} (标准¥{r.cost_per_kg}/kg)</option>)}
            </select>
            <input type="number" value={rtForm.step_order} onChange={e => setRtForm(f => ({ ...f, step_order: e.target.value }))}
              placeholder="顺序" style={{ ...IS, width: '70px' }} />
            <input type="number" value={rtForm.override_cost} onChange={e => setRtForm(f => ({ ...f, override_cost: e.target.value }))}
              placeholder="覆盖成本（留空用标准值）" style={{ ...IS, width: '180px' }} />
            <button onClick={addRt} className="px-3 py-1 text-xs font-bold rounded-sm" style={{ background: '#60A5FA', color: '#fff' }}>添加</button>
            <button onClick={() => setAddingRt(false)} className="px-3 py-1 text-xs rounded-sm" style={{ background: '#FFFFFF', color: '#5A6780', border: '1px solid #D0D5E2' }}>取消</button>
          </div>
        )}
        <div className="space-y-1.5">
          {recipeRoutings.map((rr, i) => {
            const cost = rr.override_cost != null ? Number(rr.override_cost) : (rr.process_routing?.cost_per_kg ?? 0);
            return (
              <div key={rr.id} className="flex items-center gap-3 px-3 py-2 rounded-sm"
                style={{ background: '#FFFFFF', border: '1px solid #D0D5E2' }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0"
                  style={{ background: `${ORANGE}20`, color: ORANGE }}>{i + 1}</span>
                <span className="flex-1 text-xs font-medium" style={{ color: '#1A2337' }}>{rr.process_routing?.name ?? '—'}</span>
                {rr.process_routing?.duration_min && (
                  <span className="text-xs" style={{ color: '#5A6780' }}>{rr.process_routing.duration_min}min</span>
                )}
                <span className="text-xs font-bold tabular-nums" style={{ color: rr.override_cost != null ? '#FBBF24' : '#C5D0E0' }}>
                  ¥{Number(cost).toFixed(2)}/kg {rr.override_cost != null && '(自定义)'}
                </span>
                <button onClick={() => delRt(rr.id)} className="p-1" style={{ color: '#F87171' }}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── 配方列表 Tab ──────────────────────────────────────
function RecipesTab({
  recipes, materials, routings, onRefresh,
}: {
  recipes: ProductRecipe[]; materials: RawMaterial[]; routings: ProcessRouting[]; onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<ProductRecipe>>({ material_type: 'FKM', is_active: true });
  const [techForm, setTechForm] = useState<Record<string, string>>(
    Object.fromEntries(ALL_SPEC_KEYS.map(k => [k, '']))
  );
  const [newMatCustom, setNewMatCustom] = useState('');
  const [newDensityNum, setNewDensityNum] = useState('');
  const [newHardnessNum, setNewHardnessNum] = useState('');
  const [editingRecipeId, setEditingRecipeId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ProductRecipe>>({});
  const [editMatCustom, setEditMatCustom] = useState('');
  const [editDensityNum, setEditDensityNum] = useState('');
  const [editHardnessNum, setEditHardnessNum] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const save = async () => {
    if (!form.product_code || !form.product_name || !form.material_type) return;
    setSaving(true);
    const techPatch = Object.fromEntries(
      ALL_SPEC_KEYS.map(k => [k, techForm[k]?.trim() || null])
    );
    const finalType = form.material_type === '自定义' ? newMatCustom : (form.material_type ?? 'FKM');
    const finalDensity = newDensityNum ? `${newDensityNum} g/cm³` : null;
    const finalHardness = newHardnessNum ? `${newHardnessNum} Shore A` : null;
    await supabase.from('product_recipes').insert({
      product_code: form.product_code, product_name: form.product_name,
      material_type: finalType, spec_thickness: finalDensity,
      spec_hardness: finalHardness, notes: form.notes ?? null, is_active: true,
      ...techPatch,
    });
    setSaving(false); setAdding(false);
    setForm({ material_type: 'FKM', is_active: true });
    setTechForm(Object.fromEntries(ALL_SPEC_KEYS.map(k => [k, ''])));
    setNewMatCustom(''); setNewDensityNum(''); setNewHardnessNum('');
    onRefresh();
  };

  const openEditRecipe = (recipe: ProductRecipe) => {
    const isCustom = !['FKM','EPDM','NBR','NR','CR','ESD'].includes(recipe.material_type);
    setEditingRecipeId(recipe.id);
    setEditForm({
      product_code: recipe.product_code,
      product_name: recipe.product_name,
      material_type: isCustom ? '自定义' : recipe.material_type,
    });
    setEditMatCustom(isCustom ? recipe.material_type : '');
    const dm = recipe.spec_thickness?.match(/^([\.\d]+)/);
    setEditDensityNum(dm ? dm[1] : '');
    const hm = recipe.spec_hardness?.match(/^([\.\d]+)/);
    setEditHardnessNum(hm ? hm[1] : '');
  };

  const saveEditRecipe = async () => {
    if (!editForm.product_code || !editForm.product_name || !editingRecipeId) return;
    setEditSaving(true);
    const finalType = editForm.material_type === '自定义' ? editMatCustom : (editForm.material_type ?? '');
    const finalDensity = editDensityNum ? `${editDensityNum} g/cm³` : null;
    const finalHardness = editHardnessNum ? `${editHardnessNum} Shore A` : null;
    await supabase.from('product_recipes').update({
      product_code: editForm.product_code,
      product_name: editForm.product_name,
      material_type: finalType,
      spec_thickness: finalDensity,
      spec_hardness: finalHardness,
    }).eq('id', editingRecipeId);
    setEditSaving(false);
    setEditingRecipeId(null);
    onRefresh();
  };

  const del = async (id: string) => {
    if (!confirm('确认删除此配方？')) return;
    await supabase.from('product_recipes').delete().eq('id', id);
    onRefresh();
  };

  const typeColor = MATERIAL_TYPE_COLOR;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>
          产品配方库 ({recipes.length} 条)
        </p>
        <button onClick={() => setAdding(a => !a)}
          className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm"
          style={{ background: ORANGE, color: '#fff' }}>
          <Plus className="w-3.5 h-3.5" /> 新建配方
        </button>
      </div>

      {adding && (
        <div className="rounded-sm border p-4 space-y-3" style={{ background: '#F7F8FA', borderColor: `${ORANGE}50` }}>
          <p className="text-xs font-bold" style={{ color: ORANGE }}>新建产品配方</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>产品编号 *</label>
              <input value={form.product_code ?? ''} onChange={e => setForm(f => ({ ...f, product_code: e.target.value }))} style={IS} placeholder="FKM-STD-3MM" /></div>
            <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>产品名称 *</label>
              <input value={form.product_name ?? ''} onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))} style={IS} placeholder="FKM氟橡胶板 3mm" /></div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>材料类型 *</label>
              <select value={form.material_type ?? 'FKM'} onChange={e => setForm(f => ({ ...f, material_type: e.target.value }))} style={IS}>
                {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t === '自定义' ? '自定义…' : t}</option>)}
              </select>
              {form.material_type === '自定义' && (
                <input value={newMatCustom} onChange={e => setNewMatCustom(e.target.value)}
                  style={{ ...IS, marginTop: 4 }} placeholder="填写材料类型，如 SBR" />
              )}
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>密度规格</label>
              <div className="flex items-center gap-1">
                <input type="number" step="0.01" min="0" value={newDensityNum}
                  onChange={e => setNewDensityNum(e.target.value)}
                  style={{ ...IS, flex: 1 }} placeholder="1.40" />
                <span style={{ color: '#5A6780', fontSize: 12, whiteSpace: 'nowrap' }}>g/cm³</span>
              </div>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>硬度规格</label>
              <div className="flex items-center gap-1">
                <input type="number" step="1" min="0" max="100" value={newHardnessNum}
                  onChange={e => setNewHardnessNum(e.target.value)}
                  style={{ ...IS, flex: 1 }} placeholder="70" />
                <span style={{ color: '#5A6780', fontSize: 12, whiteSpace: 'nowrap' }}>Shore A</span>
              </div>
            </div>

            {/* ── 技术预设分隔线 ── */}
            <div className="col-span-2 md:col-span-3 pt-2">
              <div className="flex items-center gap-2">
                <div style={{ width: 3, height: 12, background: ORANGE, borderRadius: 1 }} />
                <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>
                  技术预设 · Technical Specs（选填）
                </p>
              </div>
            </div>

            {/* ── 智能控件区（全宽）── */}
            <div className="col-span-2 md:col-span-3">
              <TechSpecsSmartForm
                values={techForm}
                onChange={(k, v) => setTechForm(f => ({ ...f, [k]: v }))}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving}
              className="flex items-center gap-1.5 text-xs font-bold px-4 py-1.5 rounded-sm disabled:opacity-60"
              style={{ background: ORANGE, color: '#fff' }}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} 创建配方
            </button>
            <button onClick={() => setAdding(false)} className="text-xs px-4 py-1.5 rounded-sm"
              style={{ background: '#FFFFFF', color: '#5A6780', border: '1px solid #D0D5E2' }}>取消</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {recipes.map(recipe => (
          <div key={recipe.id} className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer"
              onClick={() => setExpanded(e => e === recipe.id ? null : recipe.id)}>
              <span className="text-xs font-bold px-2 py-0.5 rounded-sm"
                style={{ background: `${typeColor[recipe.material_type] ?? '#8A9BB5'}20`, color: typeColor[recipe.material_type] ?? '#8A9BB5' }}>
                {recipe.material_type}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold" style={{ color: '#1A2337' }}>{recipe.product_name}</p>
                <p className="text-xs" style={{ color: '#5A6780' }}>
                  {recipe.product_code}
                  {recipe.spec_thickness && ` · 密度: ${recipe.spec_thickness}`}
                  {recipe.spec_hardness && ` · ${recipe.spec_hardness}`}
                </p>
                {ALL_SPEC_KEYS.some(k => !!(recipe as unknown as Record<string,unknown>)[k]) && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {ALL_SPEC_KEYS.map(k => {
                      const val = (recipe as unknown as Record<string,unknown>)[k] as string | null;
                      if (!val) return null;
                      const cfg = SPEC_TAG_CFG[k];
                      return (
                        <span key={k} style={{
                          background: cfg?.bg ?? 'rgba(90,103,128,0.1)',
                          color: cfg?.color ?? '#5A6780',
                          border: `1px solid ${cfg?.color ?? '#5A6780'}40`,
                          borderRadius: 4, padding: '1px 6px',
                          fontSize: 10, fontWeight: 600,
                        }}>{cfg?.label ?? k}: {val}</span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={e => { e.stopPropagation(); openEditRecipe(recipe); }}
                  className="p-1.5 rounded-sm" style={{ color: '#60A5FA' }} title="编辑基本信息">
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={e => { e.stopPropagation(); del(recipe.id); }} className="p-1.5 rounded-sm" style={{ color: '#F87171' }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                {expanded === recipe.id ? <ChevronUp className="w-4 h-4" style={{ color: '#5A6780' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#5A6780' }} />}
              </div>
            </div>
            {editingRecipeId === recipe.id && (
              <div className="border-t px-4 py-3 space-y-3" style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}>
                <p className="text-xs font-bold" style={{ color: '#1D4ED8' }}>✏️ 编辑基本信息</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>产品编号 *</label>
                    <input value={editForm.product_code ?? ''} onChange={e => setEditForm(f => ({ ...f, product_code: e.target.value }))} style={IS} /></div>
                  <div><label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>产品名称 *</label>
                    <input value={editForm.product_name ?? ''} onChange={e => setEditForm(f => ({ ...f, product_name: e.target.value }))} style={IS} /></div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>材料类型 *</label>
                    <select value={editForm.material_type ?? 'FKM'} onChange={e => setEditForm(f => ({ ...f, material_type: e.target.value }))} style={IS}>
                      {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t === '自定义' ? '自定义…' : t}</option>)}
                    </select>
                    {editForm.material_type === '自定义' && (
                      <input value={editMatCustom} onChange={e => setEditMatCustom(e.target.value)}
                        style={{ ...IS, marginTop: 4 }} placeholder="填写材料类型" />
                    )}
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>密度规格</label>
                    <div className="flex items-center gap-1">
                      <input type="number" step="0.01" min="0" value={editDensityNum}
                        onChange={e => setEditDensityNum(e.target.value)} style={{ ...IS, flex: 1 }} placeholder="1.40" />
                      <span style={{ color: '#5A6780', fontSize: 12 }}>g/cm³</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: '#5A6780' }}>硬度规格</label>
                    <div className="flex items-center gap-1">
                      <input type="number" step="1" min="0" max="100" value={editHardnessNum}
                        onChange={e => setEditHardnessNum(e.target.value)} style={{ ...IS, flex: 1 }} placeholder="70" />
                      <span style={{ color: '#5A6780', fontSize: 12 }}>Shore A</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveEditRecipe} disabled={editSaving}
                    className="flex items-center gap-1.5 text-xs font-bold px-4 py-1.5 rounded-sm disabled:opacity-60"
                    style={{ background: '#1D4ED8', color: '#fff' }}>
                    {editSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} 保存
                  </button>
                  <button onClick={() => setEditingRecipeId(null)} className="text-xs px-4 py-1.5 rounded-sm"
                    style={{ background: '#FFFFFF', color: '#5A6780', border: '1px solid #D0D5E2' }}>取消</button>
                </div>
              </div>
            )}
            {expanded === recipe.id && (
              <div className="border-t border-border px-4 pb-4" style={{ background: '#F7F8FA' }}>
                <RecipeDetail recipe={recipe} materials={materials} routings={routings} />
              </div>
            )}
          </div>
        ))}
        {recipes.length === 0 && (
          <div className="flex flex-col items-center py-10 gap-2">
            <FlaskConical className="w-8 h-8" style={{ color: '#D0D5E2' }} />
            <p className="text-xs" style={{ color: '#3A4A5C' }}>暂无配方，点击"新建配方"开始</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 工艺路线 Tab ──────────────────────────────────────
function RoutingsTab({ routings, onRefresh }: { routings: ProcessRouting[]; onRefresh: () => void }) {
  return (
    <div className="space-y-4">
      <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>
        标准工艺路线库 ({routings.length} 条) — 可在配方中按需覆盖成本
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
              {['顺序', '编号', '工艺名称', '标准工时', '标准加工费', '说明'].map(h => (
                <th key={h} className="text-left py-2 pr-4 font-mono" style={{ color: '#5A6780' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {routings.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td className="py-2 pr-4">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black inline-flex"
                    style={{ background: `${ORANGE}20`, color: ORANGE }}>{r.step_order}</span>
                </td>
                <td className="py-2 pr-4 font-mono text-[10px]" style={{ color: '#5A6780' }}>{r.code}</td>
                <td className="py-2 pr-4 font-bold" style={{ color: '#1A2337' }}>{r.name}</td>
                <td className="py-2 pr-4 tabular-nums" style={{ color: '#C5D0E0' }}>{r.duration_min ? `${r.duration_min} min` : '—'}</td>
                <td className="py-2 pr-4 tabular-nums font-bold" style={{ color: '#34D399' }}>¥{Number(r.cost_per_kg).toFixed(2)}/kg</td>
                <td className="py-2" style={{ color: '#5A6780', maxWidth: '240px' }}>{r.description ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 主组件 ────────────────────────────────────────────
export function RecipesView() {
  const [tab, setTab] = useState<Tab>('recipes');
  const [recipes, setRecipes] = useState<ProductRecipe[]>([]);
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [routings, setRoutings] = useState<ProcessRouting[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true); setErr('');
    const [{ data: r }, { data: m }, { data: rt }] = await Promise.all([
      supabase.from('product_recipes').select('*').order('material_type').order('product_code'),
      supabase.from('raw_materials').select('*').order('category').order('name'),
      supabase.from('process_routings').select('*').order('step_order'),
    ]);
    if (!r && !m && !rt) { setErr('数据加载失败'); }
    setRecipes((r ?? []) as ProductRecipe[]);
    setMaterials((m ?? []) as RawMaterial[]);
    setRoutings((rt ?? []) as ProcessRouting[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'recipes',   label: `产品配方 (${recipes.length})`,  icon: FlaskConical },
    { id: 'materials', label: `原料库 (${materials.length})`,  icon: Package2 },
    { id: 'routings',  label: `工艺路线 (${routings.length})`, icon: DollarSign },
  ];

  return (
    <div className="space-y-6">
      {/* 标题 */}
      <div className="rounded-sm border border-border p-5" style={{ background: HEADER_BG }}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-black tracking-wider" style={{ color: '#1A2337' }}>BOM & 动态成本核算</h2>
            <p className="text-xs mt-1" style={{ color: '#5A6780' }}>
              管理原料库单价 → 配置产品 BOM 比例 → 系统自动计算动态成本与建议报价
            </p>
          </div>
          <button onClick={fetchAll} disabled={loading}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm"
            style={{ background: '#FFFFFF', color: '#5A6780', border: '1px solid #D0D5E2' }}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
        </div>
      </div>

      {err && (
        <div className="flex items-center gap-2 p-4 rounded-sm border" style={{ background: '#1A0A0A', borderColor: '#F8717140' }}>
          <AlertCircle className="w-4 h-4" style={{ color: '#F87171' }} />
          <p className="text-sm" style={{ color: '#F87171' }}>{err}</p>
        </div>
      )}

      {/* Tab 切换 */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold transition-all border-b-2 -mb-px"
            style={{
              color: tab === id ? ORANGE : '#8A9BB5',
              borderColor: tab === id ? ORANGE : 'transparent',
            }}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} /></div>
      ) : (
        <div className="rounded-sm border border-border p-5" style={{ background: CARD_BG }}>
          {tab === 'recipes'   && <RecipesTab recipes={recipes} materials={materials} routings={routings} onRefresh={fetchAll} />}
          {tab === 'materials' && <MaterialsTab materials={materials} onRefresh={fetchAll} />}
          {tab === 'routings'  && <RoutingsTab routings={routings} onRefresh={fetchAll} />}
        </div>
      )}
    </div>
  );
}

export default RecipesView;